import { useEffect } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { yupResolver } from '@hookform/resolvers/yup';
import type {
  ManagementUpdate,
  ResponseError,
  RiskUpdateResult,
} from '@rtkwlf/mr-infra-risk';
import type { State } from '@rtkwlf/mr-infra-risk';
import { faRotate } from '@rtkwlf/pro-solid-svg-icons/faRotate';
import type { MutationStatus, UseMutateFunction } from '@tanstack/react-query';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import {
  NULLABLE_ID,
  NULLABLE_TIME,
  type UpdateRisks,
} from '@apis/mr-infra-risk/mutations/use-update-risks';
import { useActionDrawer } from '@packages/components/action-drawer/action-drawer-context';
import type { GridRowSelectionModel } from '@packages/fenrir/components';
import { Button, Stack } from '@packages/fenrir/components';
import { dayjs } from '@packages/utils/dayjs.utils';

type StateExtended = State | '';

export type FormValues = {
  state: StateExtended;
  assignedTo?: string;
  dueDate?: dayjs.Dayjs | null;
};

export type UpdateFormData = {
  id: string;
  state: StateExtended;
  assignee: string | null;
  deadline: Date | null;
};

export type UpdateRiskFormProps<T> = {
  drawerData: T[];
  setRowSelectionModel: (model: GridRowSelectionModel) => void;
  mutate: UseMutateFunction<
    RiskUpdateResult,
    ResponseError,
    UpdateRisks,
    unknown
  >;
  updateStatus: MutationStatus;
  actionTitle: string;
  children: React.ReactNode;
};

export const UpdateRisksForm = <T extends UpdateFormData>({
  drawerData = [],
  setRowSelectionModel,
  mutate,
  updateStatus,
  actionTitle,
  children,
}: UpdateRiskFormProps<T>) => {
  const { closeDrawer } = useActionDrawer();

  const { t } = useTranslation('riskManagement', { keyPrefix: 'risks' });
  const { t: tUi } = useTranslation('ui');

  const formMethods = useForm<FormValues>({
    defaultValues: {
      state: '',
      assignedTo: '',
      dueDate: null,
    },
    resolver: yupResolver(
      yup.object().shape({
        state: yup.mixed<StateExtended>().required(tUi('requireField')),
        assignedTo: yup.string(),
        dueDate: yup
          .mixed<dayjs.Dayjs>()
          .nullable()
          .test(
            'is-dayjs',
            t('invalidDate'),
            (value) => value === null || dayjs.isDayjs(value),
          ),
      }),
    ),
  });

  const {
    handleSubmit,
    reset,
    formState: { isDirty, dirtyFields },
  } = formMethods;

  const handleUpdate = (data: UpdateRisks, closeDrawer: () => void) => {
    mutate(data, {
      onSuccess: () => {
        closeDrawer();
        setRowSelectionModel([]);
      },
    });
  };

  useEffect(() => {
    if (drawerData.length === 0) {
      reset();
      return;
    }
    const data = drawerData[0];
    if (drawerData.length === 1) {
      reset({
        state: data.state ?? '',
        assignedTo: data.assignee ?? '',
        dueDate: data.deadline ? dayjs(data.deadline) : null,
      });
    } else {
      reset({
        state: drawerData.every((item) => item.state === data.state)
          ? data.state
          : '',
        assignedTo:
          drawerData.every((item) => item.assignee === data.assignee) &&
          data.assignee
            ? data.assignee
            : '',
        dueDate:
          drawerData.every(
            (item) => item.deadline?.getTime() === data.deadline?.getTime(),
          ) && data.deadline
            ? dayjs(data.deadline)
            : null,
      });
    }
  }, [drawerData, reset]);

  const handleClear = () => {
    reset();
    closeDrawer();
  };

  const onSubmit = (data: FormValues) => {
    const risksToBeUpdated = drawerData.map((item) => String(item.id));

    const updatedFields: ManagementUpdate = {};

    if (dirtyFields.state && data.state !== '') {
      updatedFields.state = data.state;
    }

    if (dirtyFields.assignedTo) {
      updatedFields.assignee =
        data.assignedTo !== '' ? data.assignedTo : NULLABLE_ID;
    }

    if (dirtyFields.dueDate) {
      updatedFields.deadline = data.dueDate
        ? data.dueDate.utc().toDate()
        : dayjs(NULLABLE_TIME).utc().toDate();
    }

    handleUpdate(
      {
        selectedRisks: risksToBeUpdated,
        update: updatedFields,
      },
      closeDrawer,
    );
  };

  return (
    <Stack sx={{ height: '100%', p: 4 }}>
      <FormProvider {...formMethods}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          style={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}
        >
          {children}
          <Stack
            direction="row"
            spacing={2}
            justifyContent="flex-end"
            marginTop={2}
            alignItems="center"
          >
            <Button onClick={handleClear}>{tUi('button.cancel')}</Button>

            <Button
              color="primary"
              variant="contained"
              type="submit"
              startIcon={
                <FontAwesomeIcon
                  icon={faRotate}
                  color="var(--aw-palette-common-white)"
                  aria-label="rotate"
                />
              }
              disabled={!isDirty}
              loading={updateStatus === 'pending'}
            >
              {actionTitle}
            </Button>
          </Stack>
        </form>
      </FormProvider>
    </Stack>
  );
};
